import { Box, Text } from '@chakra-ui/react';
import {
  createFilter,
  type GroupBase,
  Select,
  type SingleValue,
} from 'chakra-react-select';
import { useEffect, useMemo } from 'react';
import { useQuery } from 'urql';

import { graphql } from '@revelio/data-access';
import { getCurrencySymbol } from '@revelio/replots';

import {
  type CurrencyOption,
  useCurrencyStore,
} from './hooks/useCurrencyStore';

interface SelectOption {
  label: string;
  value: string;
  currency: CurrencyOption;
}

const CurrencySelectionList = graphql(`
  query CurrencySelectionList {
    selectionList {
      currency {
        id
        code
        name
        rate
        record_date
        effective_date
      }
    }
  }
`);

const filterOption = createFilter<SelectOption>({
  stringify: (option) => `${option.data.label} ${option.data.value}`,
});

interface CurrencySelectProps {
  inline?: boolean;
}

export const CurrencySelect = ({ inline }: CurrencySelectProps) => {
  const { options, selected, setOptions, setSelected, autoSelectByCountry } =
    useCurrencyStore();

  const [{ data, fetching }] = useQuery({ query: CurrencySelectionList });

  // Populate store with fetched currency options
  useEffect(() => {
    const list = data?.selectionList?.currency;
    if (!list) return;

    const mapped: CurrencyOption[] = list
      .filter((c): c is NonNullable<typeof c> & { id: string; code: string } =>
        Boolean(c?.id && c?.code)
      )
      .map((c) => ({
        id: c.id,
        code: c.code,
        name: (c.name ?? '').replace(/-/g, ' '),
        rate: c.rate ?? 1,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    setOptions(mapped);
  }, [data, setOptions]);

  // Detect user location and auto-select currency
  useEffect(() => {
    if (options.length === 0) return;

    // Use a lightweight geo-IP lookup (no API key needed)
    fetch('https://ipapi.co/json/')
      .then((res) => res.json())
      .then((geo: { country_code?: string }) => {
        if (geo.country_code) {
          autoSelectByCountry(geo.country_code);
        }
      })
      .catch(() => {
        // Fallback to USD on error
        autoSelectByCountry('US');
      });
  }, [options, autoSelectByCountry]);

  const selectOptions = useMemo<SelectOption[]>(
    () =>
      options.map((o) => ({
        label: `${getCurrencySymbol(o.code)} ${o.code} – ${o.name}`,
        value: o.code,
        currency: o,
      })),
    [options]
  );

  const selectedOption = useMemo(
    () => selectOptions.find((o) => o.value === selected?.code) ?? null,
    [selectOptions, selected]
  );

  const handleChange = (val: SingleValue<SelectOption>) => {
    if (val) {
      setSelected(val.currency);
    }
  };

  return (
    <Box
      {...(inline
        ? {}
        : { position: 'absolute', top: 3, right: 12, zIndex: 10 })}
      display="flex"
      alignItems="center"
      gap={2}
    >
      <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
        Currency
      </Text>
      <Box w="180px">
        <Select<SelectOption, false, GroupBase<SelectOption>>
          size="sm"
          menuPlacement="auto"
          isLoading={fetching}
          options={selectOptions}
          value={selectedOption}
          onChange={handleChange}
          filterOption={filterOption}
          placeholder="USD"
          chakraStyles={{
            container: (provided) => ({
              ...provided,
              fontSize: 'xs',
            }),
            control: (provided) => ({
              ...provided,
              minHeight: '28px',
              borderRadius: 'md',
            }),
            valueContainer: (provided) => ({
              ...provided,
              padding: '0 6px',
            }),
            indicatorSeparator: () => ({ display: 'none' }),
            dropdownIndicator: (provided) => ({
              ...provided,
              padding: '0 4px',
            }),
          }}
        />
      </Box>
    </Box>
  );
};
